import { useState, useCallback, useMemo, useEffect } from 'react';
import { apiFetch } from '../../../config/api';
import { useTemplateAnalyticsMV } from '../../TemplateManagerDashboard/hooks/useTemplateAnalyticsMV';

/**
 * Hook for experiment-related template operations using MV endpoint
 * Replaces useTemplates hook that used TemplatesDataProvider
 *
 * Features:
 * - Uses MV endpoint for pre-computed analytics
 * - Server-side pagination support
 * - Search integration
 * - Filters for active templates (experiments only use active templates)
 *
 * @param {Object} options - Configuration options
 * @param {number} options.pageSize - Number of templates per page (default: 50)
 * @param {boolean} options.enabled - Whether to fetch immediately (default: true)
 */
export const useTemplatesForExperiments = ({
  initialPageSize = 50,
  enabled = true,
} = {}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [searchQuery, setSearchQuery] = useState('');

  // Debounce search query
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
      // Reset to page 1 when search changes
      setCurrentPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const trimmedSearchQuery = debouncedSearchQuery.trim();
  const hasValidSearchQuery = trimmedSearchQuery.length >= 3;

  const {
    templates: apiTemplates,
    loading,
    error,
    totalCount: apiTotalCount,
    refetch,
  } = useTemplateAnalyticsMV({
    limit: pageSize,
    offset: (currentPage - 1) * pageSize,
    category: null,
    status: 'active', // Only active templates for experiments
    searchQuery: hasValidSearchQuery ? trimmedSearchQuery : null,
    enabled,
  });

  // Filter active templates (additional client-side filter for safety)
  const activeTemplates = useMemo(() => {
    if (!apiTemplates || apiTemplates.length === 0) return [];

    return apiTemplates.filter(t => {
      const status = t.version_status || t.status;
      return status === 'active';
    });
  }, [apiTemplates]);

  const getActiveTemplates = useCallback(() => {
    return Promise.resolve({
      templates: activeTemplates,
      total_count: activeTemplates.length,
      total_pages: Math.ceil(activeTemplates.length / pageSize),
      page: currentPage,
      page_size: pageSize,
    });
  }, [activeTemplates, currentPage, pageSize]);

  const getTemplatesForExperiment = useCallback(() => {
    // Same as getActiveTemplates for experiments
    return getActiveTemplates();
  }, [getActiveTemplates]);

  const getTemplate = useCallback(async templateId => {
    try {
      const data = await apiFetch(`/api/v1/templates/${templateId}?status=all`);
      return data;
    } catch (err) {
      throw new Error(err.message || 'Failed to fetch template');
    }
  }, []);

  const getTemplateVersions = useCallback(async templateId => {
    if (!templateId) {
      return { templates: [] };
    }
    try {
      const data = await apiFetch(`/api/v1/templates/${templateId}/versions`);
      return data;
    } catch (err) {
      // If fetch fails, return empty array instead of crashing
      return { templates: [] };
    }
  }, []);

  const fetchTemplates = useCallback(
    async (params = {}) => {
      // Refresh templates
      await refetch();
      return Promise.resolve({
        templates: activeTemplates,
        total_count: activeTemplates.length,
      });
    },
    [refetch, activeTemplates]
  );

  return {
    templates: activeTemplates,
    loading,
    error,
    totalCount: activeTemplates.length,
    apiTotalCount, // Total from API (may include all statuses)
    currentPage,
    pageSize,
    totalPages: Math.ceil((apiTotalCount || activeTemplates.length) / pageSize),
    fetchTemplates,
    getTemplate,
    getActiveTemplates,
    getTemplatesForExperiment,
    getTemplateVersions,
    setCurrentPage,
    setPageSize,
    setSearchQuery,
    searchQuery,
    refetch,
  };
};

export default useTemplatesForExperiments;
